import { useState, useEffect, useCallback } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { IconCalendarEvent, IconAlertCircle, IconRefresh, IconClock, IconSettings, IconCheck, IconBriefcase, IconPlus } from '@tabler/icons-react';
import { api } from '../../../../api/Middleware';
import { CALENDAR_URL, CALENDAR_WORKING_HOURS_URL, APPOINTMENT_TYPES_URL, BUSINESS_HOURS_SETTINGS_URL } from '../../../../api/Urls';
import WorkingHoursModal, { getHoursChip, getServicesChip } from './WorkingHoursModal';

/**
 * InternalCalendarHoursPicker
 *
 * Calendar selector + status chips for hours and services.
 * Opens WorkingHoursModal to configure both, persisting to DB:
 *   - Working hours → /calendar-working-hours/bulk-update/
 *   - Services (AppointmentType) → /appointment-types/ (create/delete per calendar)
 *
 * Props:
 *   calendarId:       number | null
 *   onCalendarChange: (id: number | null) => void
 */
const InternalCalendarHoursPicker = ({ calendarId, onCalendarChange }) => {
  const [calendars, setCalendars] = useState([]);
  const [loadingCalendars, setLoadingCalendars] = useState(true);
  const [calError, setCalError] = useState(null);

  const [hours, setHours] = useState(null);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [globalSlotDuration, setGlobalSlotDuration] = useState(30);

  // Inline calendar creation
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCalendarName, setNewCalendarName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const selectedCalendar = calendars.find((c) => c.id === calendarId);

  // -- Fetch calendar list --
  const fetchCalendars = useCallback(async () => {
    setLoadingCalendars(true);
    setCalError(null);
    try {
      const res = await api.get(CALENDAR_URL);
      setCalendars(res.data?.results || res.data || []);
    } catch {
      setCalError('Erro ao carregar agendas');
    } finally {
      setLoadingCalendars(false);
    }
  }, []);

  useEffect(() => { fetchCalendars(); }, [fetchCalendars]);

  // Fetch global slot duration (BusinessHoursSettings.appointment_duration) —
  // usado como placeholder do campo "Slot do agente" no modal.
  useEffect(() => {
    api.get(BUSINESS_HOURS_SETTINGS_URL)
      .then((res) => {
        const data = res.data?.results?.[0] || res.data?.[0] || res.data;
        const val = data?.appointment_duration;
        if (typeof val === 'number' && val > 0) setGlobalSlotDuration(val);
      })
      .catch(() => { /* mantém default 30 */ });
  }, []);

  // -- Fetch hours and services when calendar changes --
  useEffect(() => {
    if (!calendarId) {
      setHours(null);
      setAppointmentTypes([]);
      return;
    }
    setLoadingData(true);
    setSaveOk(false);
    Promise.all([
      api.get(`${CALENDAR_WORKING_HOURS_URL}?calendar_id=${calendarId}`).catch(() => ({ data: [] })),
      api.get(`${APPOINTMENT_TYPES_URL}calendar_specific/?calendar_id=${calendarId}`).catch(() => ({ data: [] })),
    ]).then(([hoursRes, typesRes]) => {
      setHours(hoursRes.data || []);
      setAppointmentTypes(typesRes.data || []);
    }).finally(() => setLoadingData(false));
  }, [calendarId]);

  // -- Create calendar inline --
  const handleCreateCalendar = async () => {
    const name = newCalendarName.trim();
    if (!name) return;
    setCreating(true);
    setCreateError(null);
    try {
      const res = await api.post(CALENDAR_URL, { name });
      const created = res.data;
      setCalendars((prev) => [...prev, created]);
      setNewCalendarName('');
      setShowCreateForm(false);
      onCalendarChange(created.id);
    } catch (err) {
      setCreateError(err.response?.data?.name?.[0] || err.response?.data?.detail || 'Erro ao criar agenda');
    } finally {
      setCreating(false);
    }
  };

  // -- Modal save: persist hours + services diff + agent_slot config to DB --
  const handleModalSave = async (newHours, newServices, newAgentSlotDuration, newAgentSlotEnabled) => {
    // 1. Working hours
    await api.post(`${CALENDAR_WORKING_HOURS_URL}bulk-update/`, {
      calendar_id: calendarId,
      days: newHours,
    });

    // 1b. Calendar.agent_slot_* — só PATCH se algo mudou (evita escrita desnecessária)
    const currentSlot = selectedCalendar?.agent_slot_duration ?? null;
    const currentEnabled = selectedCalendar?.agent_slot_enabled !== false;
    const nextEnabled = newAgentSlotEnabled !== false;
    if (currentSlot !== newAgentSlotDuration || currentEnabled !== nextEnabled) {
      await api.patch(`${CALENDAR_URL}${calendarId}/`, {
        agent_slot_duration: newAgentSlotDuration,
        agent_slot_enabled: nextEnabled,
      });
    }

    // 2. Services diff: delete removed, create new, patch alterações
    //    (show_in_agent, name, duration, price).
    const originalById = new Map(appointmentTypes.map((s) => [s.id, s]));
    const keptIds = newServices.filter((s) => s.id).map((s) => s.id);
    const toDelete = [...originalById.keys()].filter((id) => !keptIds.includes(id));
    const toCreate = newServices.filter((s) => !s.id);

    // PATCH consolidado: detecta diff em qualquer campo editável e monta payload mínimo.
    const buildPatchPayload = (next, original) => {
      const payload = {};
      const nextShow = next.showInAgent !== false;
      if ((original.show_in_agent !== false) !== nextShow) payload.show_in_agent = nextShow;
      if (next.name !== original.name) payload.name = next.name;
      const nextDur = parseInt(next.duration, 10) || 0;
      const origDur = parseInt(original.duration, 10) || 0;
      if (nextDur !== origDur) payload.duration = nextDur || 30;
      const nextPrice = next.price === '' || next.price == null ? 0 : parseFloat(next.price);
      const origPrice = original.price == null ? 0 : parseFloat(original.price);
      if (nextPrice !== origPrice) payload.price = nextPrice || 0;
      return payload;
    };
    const toPatch = newServices
      .filter((s) => s.id && originalById.has(s.id))
      .map((s) => ({ id: s.id, payload: buildPatchPayload(s, originalById.get(s.id)) }))
      .filter(({ payload }) => Object.keys(payload).length > 0);

    await Promise.all([
      ...toDelete.map((id) => api.delete(`${APPOINTMENT_TYPES_URL}${id}/`)),
      ...toCreate.map((s) =>
        api.post(APPOINTMENT_TYPES_URL, {
          name: s.name,
          duration: parseInt(s.duration) || 30,
          price: s.price ? parseFloat(s.price) : 0,
          calendar: calendarId,
          show_in_agent: s.showInAgent !== false,
        })
      ),
      ...toPatch.map(({ id, payload }) =>
        api.patch(`${APPOINTMENT_TYPES_URL}${id}/`, payload)
      ),
    ]);

    // 3. Refresh services from DB
    const refreshed = await api.get(`${APPOINTMENT_TYPES_URL}calendar_specific/?calendar_id=${calendarId}`).catch(() => ({ data: [] }));
    setHours(newHours);
    setAppointmentTypes(refreshed.data || []);

    // 3b. Atualizar agent_slot_* no objeto local do calendar
    if (currentSlot !== newAgentSlotDuration || currentEnabled !== nextEnabled) {
      setCalendars((prev) =>
        prev.map((c) =>
          c.id === calendarId
            ? { ...c, agent_slot_duration: newAgentSlotDuration, agent_slot_enabled: nextEnabled }
            : c
        )
      );
    }

    setSaveOk(true);
    setTimeout(() => setSaveOk(false), 3000);
  };

  // -- Styles --
  const sectionStyle = {
    background: '#f8fafc',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    padding: '14px',
    marginBottom: '12px',
  };
  const labelStyle = { fontWeight: 500, fontSize: '13px', marginBottom: '6px', display: 'block' };
  const configureBtn = {
    padding: '5px 10px',
    fontSize: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    color: '#475569',
    whiteSpace: 'nowrap',
  };

  if (loadingCalendars) {
    return (
      <div style={{ textAlign: 'center', padding: '12px' }}>
        <Spinner animation="border" size="sm" style={{ color: '#10b981' }} />
        <span style={{ marginLeft: '8px', fontSize: '13px', color: '#666' }}>Carregando agendas...</span>
      </div>
    );
  }

  if (calError) {
    return (
      <div style={{ ...sectionStyle, background: '#fff1f2', borderColor: '#fecaca' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconAlertCircle size={16} style={{ color: '#dc2626' }} />
          <span style={{ color: '#dc2626', fontSize: '13px', flex: 1 }}>{calError}</span>
          <button type="button" onClick={fetchCalendars} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}>
            <IconRefresh size={12} /> Tentar
          </button>
        </div>
      </div>
    );
  }

  const hoursChip = getHoursChip(hours);
  const servChip = getServicesChip(appointmentTypes);

  // -- Empty state: no calendars yet --
  if (calendars.length === 0) {
    return (
      <div style={{ ...sectionStyle, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
          <IconCalendarEvent size={16} style={{ color: '#10b981' }} />
          <label style={{ ...labelStyle, marginBottom: 0 }}>Agenda interna</label>
        </div>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>
          Nenhuma agenda cadastrada. Crie a primeira para continuar.
        </p>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input
            type="text"
            value={newCalendarName}
            onChange={(e) => setNewCalendarName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateCalendar()}
            placeholder="Nome da agenda (ex: Dr. Paulo)"
            style={{ flex: 1, padding: '6px 10px', border: '1px solid #a7f3d0', borderRadius: '6px', fontSize: '13px', outline: 'none', background: '#fff' }}
            disabled={creating}
          />
          <button
            type="button"
            onClick={handleCreateCalendar}
            disabled={!newCalendarName.trim() || creating}
            style={{ padding: '6px 12px', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#10b981', color: '#fff', opacity: (!newCalendarName.trim() || creating) ? 0.6 : 1 }}
          >
            {creating ? <Spinner animation="border" size="sm" /> : <><IconPlus size={13} /> Criar</>}
          </button>
        </div>
        {createError && (
          <p style={{ fontSize: '11px', color: '#dc2626', marginTop: '6px', marginBottom: 0 }}>{createError}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div style={sectionStyle}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
          <IconCalendarEvent size={16} style={{ color: '#10b981' }} />
          <label style={{ ...labelStyle, marginBottom: 0 }}>Agenda interna</label>
        </div>

        {/* Calendar selector + add button */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: calendarId ? '12px' : 0 }}>
          <Form.Select
            size="sm"
            value={calendarId || ''}
            onChange={(e) => onCalendarChange(e.target.value ? parseInt(e.target.value) : null)}
            style={{ fontSize: '13px', flex: 1 }}
          >
            <option value="">Selecione uma agenda...</option>
            {calendars.map((cal) => (
              <option key={cal.id} value={cal.id}>{cal.name}</option>
            ))}
          </Form.Select>
          <button
            type="button"
            title="Nova agenda"
            onClick={() => setShowCreateForm((v) => !v)}
            style={{ padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: '6px', background: showCreateForm ? '#f0fdf4' : '#fff', cursor: 'pointer', color: '#10b981', display: 'inline-flex', alignItems: 'center' }}
          >
            <IconPlus size={15} />
          </button>
        </div>

        {/* Inline create form */}
        {showCreateForm && (
          <div style={{ marginTop: '-6px', marginBottom: '10px' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                value={newCalendarName}
                onChange={(e) => setNewCalendarName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateCalendar()}
                placeholder="Nome da agenda (ex: Dr. Paulo)"
                style={{ flex: 1, padding: '5px 10px', border: '1px solid #a7f3d0', borderRadius: '6px', fontSize: '12px', outline: 'none', background: '#fff' }}
                disabled={creating}
                autoFocus
              />
              <button
                type="button"
                onClick={handleCreateCalendar}
                disabled={!newCalendarName.trim() || creating}
                style={{ padding: '5px 10px', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#10b981', color: '#fff', opacity: (!newCalendarName.trim() || creating) ? 0.6 : 1 }}
              >
                {creating ? <Spinner animation="border" size="sm" /> : 'Criar'}
              </button>
            </div>
            {createError && (
              <p style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', marginBottom: 0 }}>{createError}</p>
            )}
          </div>
        )}

        {/* Status chips + configure button */}
        {calendarId && (
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '10px' }}>
            {loadingData ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Spinner animation="border" size="sm" style={{ color: '#10b981' }} />
                <span style={{ fontSize: '12px', color: '#666' }}>Carregando...</span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                {/* Chips */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <IconClock size={12} style={{ color: hoursChip.active ? '#10b981' : '#94a3b8' }} />
                    <span style={{ fontSize: '12px', color: hoursChip.active ? '#059669' : '#94a3b8', fontWeight: hoursChip.active ? 500 : 400 }}>
                      {hoursChip.label}
                    </span>
                    {saveOk && <IconCheck size={12} style={{ color: '#10b981' }} />}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <IconBriefcase size={12} style={{ color: servChip.active ? '#10b981' : '#f59e0b' }} />
                    <span style={{ fontSize: '12px', color: servChip.active ? '#059669' : '#f59e0b' }}>
                      {servChip.label}
                    </span>
                  </div>
                </div>

                {/* Configure button */}
                <button type="button" onClick={() => setShowModal(true)} style={configureBtn}>
                  <IconSettings size={13} /> Configurar agenda
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <WorkingHoursModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleModalSave}
        calendarName={selectedCalendar?.name || ''}
        initialValue={hours}
        initialServices={appointmentTypes.map((at) => ({
          id: at.id,
          name: at.name,
          duration: at.duration,
          price: at.price,
          showInAgent: at.show_in_agent !== false,
        }))}
        showAgentToggle
        initialAgentSlotDuration={selectedCalendar?.agent_slot_duration ?? null}
        initialAgentSlotEnabled={selectedCalendar?.agent_slot_enabled !== false}
        globalSlotDuration={globalSlotDuration}
      />
    </>
  );
};

export default InternalCalendarHoursPicker;
